import { createContext, useReducer, useContext, useEffect, useMemo } from 'react';
import useSWR from 'swr';
import { jsonFetcher, postFetch, getFetch, useHandleSWR, buildUrlWithParams } from '@neko-ui';
import { apiUrl as API, restNonce, core, coreCount, dbPrefix, options as settingsOptions } from '@app/settings';
import { takeNap, paging, camelToSnake, snakeToCamel, sortIgnoreItemToEnd } from '@app/helper';

/****************************************
  Consts
****************************************/

export const countBusyValue = (item) => `${item}_count`;
export const deleteBusyValue = (item) => `${item}_delete`;
export const cleanOptionKey = (item) => `${item}_clean_style`;
export const totalDbSizeKey = 'totalDbSize';
export const hideUsedByWordPressLabel = 'Hide items used by WordPress';

export const columns = [
  { accessor: 'details', title: 'Details', sortable: false, width: '' },
  { accessor: 'count', title: 'Count', sortable: false, width: '100px', align: 'right' },
  { accessor: 'clean', title: 'Mode', sortable: false, width: '80px' },
  { accessor: 'action', title: '', sortable: false, width: '80px' },
];

export const columnsForEasy = [
  { accessor: 'details', title: 'Details', sortable: false, width: '' },
  { accessor: 'count', title: 'Count', sortable: false, width: '80px', align: 'right' },
  { accessor: 'action', title: '', sortable: false, width: '80px' },
];

export const postTypesColumns = [
  { accessor: 'details', title: 'Post Type', sortable: true, width: '450px' },
  { accessor: 'usedBy', title: 'Used By', sortable: true, width: '400px' },
  { accessor: 'count', title: 'Count', sortable: false, width: '80px', align: 'right' },
  { accessor: 'clean', title: 'Mode', sortable: false, width: '80px' },
  { accessor: 'action', title: '', sortable: false, width: '80px' },
];

export const dbSizesColumns = [
  { accessor: 'deletion', title: '', sortable: false, width: '40px' },
  { accessor: 'table', title: 'Table', sortable: true, width: '400px' },
  { accessor: 'usedBy', title: 'Used By', sortable: true, width: '300px' },
  { accessor: 'size', title: 'Size', sortable: true, width: '80px', align: 'right' },
  { accessor: 'percent', title: 'Relative %', sortable: false, width: '80px', align: 'right' },
  { accessor: 'actions', title: '', sortable: false, width: '200px' },
];

export const dbSizesColumnsForEasy = [
  { accessor: 'table', title: 'Table', sortable: true, width: '450px' },
  { accessor: 'usedBy', title: 'Used By', sortable: true, width: '300px' },
  { accessor: 'size', title: 'Size', sortable: true, width: '80px', align: 'right' },
  { accessor: 'percent', title: 'Relative %', sortable: false, width: '80px', align: 'right' },
  { accessor: 'actions', title: '', sortable: false, width: '200px' },
];

export const optionsColumns = [
  { accessor: 'actions', title: '', sortable: false, width: '30px' },
  { accessor: 'name', title: 'Name', sortable: true, width: '450px' },
  { accessor: 'usedBy', title: 'Used By', sortable: true, width: '400px' },
  { accessor: 'size', title: 'Size', sortable: true, width: '85px', align: 'right' },
  { accessor: 'autoloaded', title: 'Auto', sortable: true, width: '55px' }
];

export const cronJobsColumns = [
  { accessor: 'actions', title: '', sortable: false, width: '30px' },
  { accessor: 'name', title: 'Name', sortable: true, width: '450px' },
  { accessor: 'usedBy', title: 'Used By', sortable: true, width: '400px' },
  { accessor: 'schedule', title: 'Schedule', sortable: false, width: '100px' },
  { accessor: 'nextRun', title: 'Next Run', sortable: false, width: '180px' }
];

export const cleanStyles = [
  { label: 'Auto', value: 'auto' },
  { label: 'Manual', value: 'manual' },
  { label: 'Never', value: 'never' },
];

export const ages = [
  { label: 'None', value: 'none' },
  { label: '1 day', value: '1 day' },
  { label: '7 days', value: '7 days' },
  { label: '1 month', value: '1 month' },
  { label: '1 year', value: '1 year' },
];

export const optionsFilters = [
  { title: 'All', value: 'all', filterValue: '' },
  { title: 'Not Used', value: 'notUsed', filterValue: 'warn'},
  { title: 'Used', value: 'used', filterValue: 'ok'},
  { title: 'Autoloaded', value: 'yes', filterValue: 'yes'},
  //{ title: 'Not Autoloaded', value: 'no', filterValue: 'no'},
  { title: 'Unknown', value: 'unknown', filterValue: 'n/a'},
];

export const cronsFilters = [
  { title: 'All', value: 'all', filterValue: '' },
  { title: 'Not Used', value: 'notUsed', filterValue: 'warn'},
  { title: 'Used', value: 'used', filterValue: 'ok'},
  { title: 'Unknown', value: 'unknown', filterValue: 'n/a'},
];

export const tablesFilters = [
  { title: 'All', value: 'all', filterValue: ''},
  { title: 'Not Used', value: 'notUsed', filterValue: 'warn'},
  { title: 'Used', value: 'used', filterValue: 'ok'},
  { title: 'Unknown', value: 'unknown', filterValue: 'n/a'},
];

export const postTypesFilters = [
  { title: 'All', value: 'all', filterValue: '' },
  { title: 'Not Used', value: 'notUsed', filterValue: 'warn'},
  { title: 'Used', value: 'used', filterValue: 'ok'},
  { title: 'Unknown', value: 'unknown', filterValue: 'n/a'},
];

export const modeOptions = {
  on: {
    label: 'Expert',
    value: 'expert',
  },
  off: {
    label: 'Easy',
    value: 'easy'
  }
};

export const viewableSQLColumns = [
  // Posts
  'ID', 'post_title', 'post_content', 'post_name', 'post_date', 'post_status', 'post_type',
  // Options
  'option_id', 'option_name', 'autoload',
  // Posts Metadata,
  'meta_id', 'meta_key', 'post_id',
  // Terms
  'object_id', 'term_order', 'term_taxonomy_id',
  // Comments,
  'comment_ID', 'comment_post_ID', 'comment_author', 'comment_date',
  // Users
  'user_id', 'meta_key'
];

/****************************************
  Functions
****************************************/
const sortByString = (a, b, sort) => {
  if (a < b) {
    return sort === 'asc' ? -1 : 1;
  }
  if (a > b) {
    return sort === 'asc' ? 1 : -1;
  }
  return 0;
}
const sortByNumber = (a, b, sort) => {
  const numberA = Number(a);
  const numberB = Number(b);
  return sort === 'asc' ? numberB - numberA : numberA - numberB;
}

const sortItems = (list, accessor, sort) => {
  if (!accessor && !sort) return [...list];

  switch (accessor) {

    case "usedBy":
      return list.sort((a, b) => sortByString(a.info.usedBy.toUpperCase(), b.info.usedBy.toUpperCase(), sort));

    case "details": // Post Type
      return list.sort((a, b) => sortByString(a.name.toUpperCase(), b.name.toUpperCase(), sort));

    case "table": // Table
      return list.sort((a, b) => sortByString(a.table.toUpperCase(), b.table.toUpperCase(), sort));

    case "size": // Table or Option
      return list.sort((a, b) => {
        if (a.hasOwnProperty('size')) {
          return sortByNumber(a.size, b.size, sort);
        } else if (a.hasOwnProperty('option_value_length')) {
          return sortByNumber(a.option_value_length, b.option_value_length, sort);
        }
        return 0;
      });

    case "name": // Cron Job or Option
      return list.sort((a, b) => {
        if (a.hasOwnProperty('cron_name')) {
          return sortByString(a.cron_name.toUpperCase(), b.cron_name.toUpperCase(), sort);
        } else if (a.hasOwnProperty('option_name')) {
          return sortByString(a.option_name.toUpperCase(), b.option_name.toUpperCase(), sort);
        }
        return 0;
      })

    case "autoloaded":
      return list.sort((a, b) => sortByString(b.autoload, a.autoload, sort));
  }
  return [...list];
}
// Filters
const filterItems = (list, filterBy, filtersDef, hideUsedByWordPress, search) => {
  let targetList = [...list];

  if (hideUsedByWordPress) {
    targetList = filterWithUsedBy([...targetList], 'wordpress');
  }

  if (search) {
    targetList = filterWithSearchText([...targetList], search);
  }

  const filterValue = filtersDef.find(v => v.value === filterBy)?.filterValue ?? '';
  if (!filterValue) return [...targetList];
  if (['yes', 'no'].includes(filterValue)) {
    return targetList.filter(v => v.autoload === filterValue);
  }
  return targetList.filter(v => v.info?.status === filterValue);
}

const filterWithUsedBy = (list, value) => {
  return list.filter(v => v.info?.usedBy.toLowerCase() !== value);
}

const filterWithSearchText = (list, value) => {
  return list.filter(v => {
    // Cron Jobs
    if (v.hasOwnProperty('cron_name') && v.cron_name.includes(value)) {
      return true;
    }
    // Tables
    if (v.hasOwnProperty('table') && v.table.includes(value)) {
      return true;
    }
    // Post Types
    if (v.hasOwnProperty('name') && v.name.includes(value)) {
      return true;
    }
    // Option Names
    if (v.hasOwnProperty('option_name') && v.option_name.includes(value)) {
      return true;
    }
    return v.info.usedBy.toLowerCase().includes(value.toLowerCase());
  });
}

// Load items
const coreKeys = Object.keys(coreCount).map(k => snakeToCamel(k));
const findKey = (obj, item) => Object.keys(obj).find(k => obj[k].some(v => v.item === item));
const findInList = (list, key) => list?.find(v => typeof v.count !== 'undefined' && Number(v.count) > 0)?.[key] ?? null;
const findNextOrPrev = (list, key, value, nextOrPrev) => {
  const currentIndex = list.findIndex(v => v[key] === value);
  const haystack = (currentIndex === -1) ? [...list] : [...list.slice(currentIndex + 1), ...list.slice(0, currentIndex)];
  if (nextOrPrev === 'prev') {
    haystack.reverse();
  }
  return findInList(haystack, key);
}
const getFirstLoadEntryItem = (currentItem, loadEntryItems) => {
  const currentItemKey = findKey(loadEntryItems, currentItem);
  if (!coreKeys.includes(currentItemKey)) {
    return null;
  }
  return findInList(loadEntryItems[coreKeys.at(0)], 'item');
}
const getLastLoadEntryItem = (currentItem, loadEntryItems) => {
  const currentItemKey = findKey(loadEntryItems, currentItem);
  if (!coreKeys.includes(currentItemKey)) {
    return null;
  }
  const list = loadEntryItems[coreKeys.at(-1)];
  list.reverse();
  return findInList(list, 'item');
}

const makeLoadingItemKeys = (key, nextOrPrev) => {
  const currentIndex = coreKeys.findIndex(k => k === key);
  const newLoadingItemKeys = [...coreKeys.slice(currentIndex), ...coreKeys.slice(0, currentIndex)];
  if (nextOrPrev === 'prev') {
    newLoadingItemKeys.reverse();
  }
  return newLoadingItemKeys;
}

const getNextOrPrevLoadEntryItem = (currentItem, loadEntryItems, nextOrPrev = 'next') => {
  const currentItemKey = findKey(loadEntryItems, currentItem);
  if (!coreKeys.includes(currentItemKey)) {
    return findNextOrPrev([...loadEntryItems[currentItemKey]], 'item', currentItem, nextOrPrev);
  }
  const keys = makeLoadingItemKeys(currentItemKey, nextOrPrev);
  let returnItem = undefined;
  keys.some(key => {
    const item = findNextOrPrev([...loadEntryItems[key]], 'item', currentItem, nextOrPrev);
    if (item) {
      returnItem = item;
    }
    return !!item;
  });
  return returnItem;
}

// DB Prefix
const removeDbPrefix = (table) => table.replace(new RegExp(`^${dbPrefix}`), '');
const addDbPrefix = (table) => `${dbPrefix}${table}`;

/****************************************
  Defaults and other settings
****************************************/

const clickedTabs ={
  Welcome: 'Welcome',
  Tables: 'Tables',
  WordPressCore: 'WordPressCore',
  PostTypes: 'PostTypes',
  Options: 'Options',
  Settings: 'Settings',
  Logs: 'Logs',
  CustomQueries: 'CustomQueries'
};

const defaultClickedTab = {
  Welcome: 0,
  Tables: 0,
  WordPressCore: 0,
  PostTypes: 0,
  Options: 0,
  Settings: 0,
  Logs: 0
};

const loadEntryItemKeys = {
  posts: 'posts',
  postsMetadata: 'postsMetadata',
  users: 'users',
  comments: 'comments',
  transients: 'transients',
  listPostTypes: 'listPostTypes',
  options: 'options',
}

const defaultModalTitles = {
  deleteAll: 'Delete All',
  loadEntry: 'Data',
  usedBy: 'Used By',
  optionValue: 'Option data',
  table: 'Table',
};

/****************************************
  Initial state
****************************************/

let busyCounter = 0;
let busyBulkTaskCounter = 0;
let busyCleanBulkTaskCounter = 0;
let busyOptionsCounter = 0;
let busyCronJobsCounter = 0;

const initialState = {
  busy: false,
  busyAction: [],
  busyBulkTasks: false,
  busyCleanBulkTasks: false,
  busyOptions: false,
  busyCronJobs: false,
  apiErrors: null,
  status: '',
  settingsOptions: settingsOptions,

  clickedTab: defaultClickedTab,
  fileContent: '',

  agaThreshold: settingsOptions.aga_threshold,
  bulkBatchSize: settingsOptions.bulk_batch_size,
  deepDeletions: settingsOptions.deep_deletions,
  mode: settingsOptions.mode,
  postTypesUsedBy: settingsOptions.post_type_usedby,
  optionsUsedBy: settingsOptions.option_usedby,
  tablesUsedBy: settingsOptions.table_usedby,
  cronJobsUsedBy: settingsOptions.cron_usedby,
  totalSizes: settingsOptions.db_historical_sizes,
  hideMessage: settingsOptions.hide_message,
  delay: settingsOptions.delay,

  customQueries: settingsOptions.custom_queries,
  customQueriesCount: [],
  editCustomQueries: [],

  dbSizes: [],
  previousTotalSize: settingsOptions.previousTotalSize,
  totalSize: settingsOptions.totalSize,

  listPostTypes: [],
  listPostTypesCount: [],
  posts: core.posts,
  postsCount: coreCount.posts,
  postsMetadata: core.posts_metadata,
  postsMetadataCount: coreCount.posts_metadata,
  users: core.users,
  usersCount: coreCount.users,
  comments: core.comments,
  commentsCount: coreCount.comments,
  transients: core.transients,
  transientsCount: coreCount.transients,
  options: [],
  filteredOptions: [],
  filteredCronJobs: [],
  filteredListPostTypes: [],
  filteredTables: [],
  entries: [],
  entriesTableColumns: [],
  cronJobs: [],
  plugins: [],

  selectedUsedBy: {
    items: [],
    value: "unknown"
  },

  initialized: {
    PostTypes: false,
  },

  modals: {
    deleteAll: {
      enable: false,
      title: defaultModalTitles.deleteAll,
      onNext: () => {}
    },
    loadEntry: {
      enable: false,
      title: defaultModalTitles.loadEntry,
      onNext: () => {}
    },
    usedBy: {
      enable: false,
      title: defaultModalTitles.usedBy,
      onNext: () => {}
    },
    optionValue: {
      enable: false,
      title: defaultModalTitles.optionValue,
      onNext: () => {}
    },
    table: {
      enable: false,
      title: defaultModalTitles.table,
      onNext: () => {},
      data: {
        tableName: '',
      }
    }
  },
  loadEntryItems: {},
  loadEntryItem: null,
  loadEntryPrevItem: null,
  loadEntryNextItem: null,
  loadEntryItemCount: 0,
  loadEntryItemPage: 1,
  loadEntryItemLimit: 10,

  mutateDbSizes: async () => {},
  mutateListPostTypes: async () => {},
  mutatePosts: async () => {},
  mutatePostsMetadata: async () => {},
  mutateUsers: async () => {},
  mutateComments: async () => {},
  mutateTransients: async () => {},
  mutateOptions: async () => {},
  mutateCronJobs: async () => {},
  mutatePlugins: async () => {},

  shouldFetchOptions: false,
  shouldFetchCronJobs: false,
  optionsFilters: {
    page: 1,
    limit: Number(settingsOptions.options_limit),
    filterBy: 'all',
    hideUsedByWordPress: false,
    search: '',
    sort: '',
    accessor: '',
  },
  optionsTotal: {
    all: '-',
    yes: '-',
    no: '-'
  },
  cronJobsFilters: {
    page: 1,
    limit: Number(settingsOptions.cron_jobs_limit),
    filterBy: 'all',
    hideUsedByWordPress: false,
    search: '',
    sort: '',
    accessor: '',
  },
  cronJobsTotal: {
    all: '-',
    yes: '-',
    no: '-'
  },
  tablesFilters: {
    page: 1,
    limit: Number(settingsOptions.db_historical_sizes_limit),
    filterBy: 'all',
    hideUsedByWordPress: false,
    search: '',
    sort: '',
    accessor: '',
  },
  tablesTotal: {
    active: '-',
    inactive: '-',
    unknown: '-',
  },
  listPostTypesFilters: {
    page: 1,
    limit: Number(settingsOptions.list_post_types_limit),
    filterBy: 'all',
    hideUsedByWordPress: false,
    search: '',
    sort: '',
    accessor: '',
  },
  listPostTypesTotal: {
    all: '-',
    yes: '-',
    no: '-'
  }
};

/****************************************
  Action types
****************************************/

const STATUS_UPDATED = 'STATUS_UPDATED';
const SETTINGS_UPDATED = 'SETTINGS_UPDATED';
const DB_SIZES_UPDATED = 'DB_SIZES_UPDATED';
const LIST_POST_TYPES_UPDATED = 'LIST_POST_TYPES_UPDATED';
const LIST_POST_TYPES_COUNT_UPDATED = 'LIST_POST_TYPES_COUNT_UPDATED';
const POSTS_UPDATED = 'POSTS_UPDATED';
const POSTS_COUNT_UPDATED = 'POSTS_COUNT_UPDATED';
const POSTS_METADATA_UPDATED = 'POSTS_METADATA_UPDATED';
const POSTS_METADATA_COUNT_UPDATED = 'POSTS_METADATA_COUNT_UPDATED';
const USERS_UPDATED = 'USERS_UPDATED';
const USERS_COUNT_UPDATED = 'USERS_COUNT_UPDATED';
const COMMENTS_UPDATED = 'COMMENTS_UPDATED';
const COMMENTS_COUNT_UPDATED = 'COMMENTS_COUNT_UPDATED';
const TRANSIENTS_UPDATED = 'TRANSIENTS_UPDATED';
const TRANSIENTS_COUNT_UPDATED = 'TRANSIENTS_COUNT_UPDATED';
const OPTIONS_UPDATED = 'OPTIONS_UPDATED';
const FILTERED_OPTIONS_UPDATED = 'FILTERED_OPTIONS_UPDATED';
const FILTERED_CRON_JOBS_UPDATED = 'FILTERED_CRON_JOBS_UPDATED';
const FILTERED_POST_TYPES_UPDATED = 'FILTERED_POST_TYPES_UPDATED';
const FILTERED_TABLES_UPDATED = 'FILTERED_TABLES_UPDATED';
const CUSTOM_QUERIES_COUNT_UPDATED = 'CUSTOM_QUERIES_COUNT_UPDATED';
const EDIT_CUSTOM_QUERIES_UPDATED = 'EDIT_CUSTOM_QUERIES_UPDATED';
const CLICKED_TAB_UPDATED = 'CLICKED_TAB_UPDATED';
const FILE_CONTENT_UPDATED = 'FILE_CONTENT_UPDATED';
const BUSY_ACTION_UPDATED = 'BUSY_ACTION_UPDATED';
const OPTIONS_FILTERS_UPDATED = 'OPTIONS_FILTERS_UPDATED';
const OPTIONS_TOTAL_UPDATED = 'OPTIONS_TOTAL_UPDATED';
const CRON_JOBS_FILTERS_UPDATED = 'CRON_JOBS_FILTERS_UPDATED';
const CRON_JOBS_TOTAL_UPDATED = 'CRON_JOBS_TOTAL_UPDATED';
const TABLES_FILTERS_UPDATED = 'TABLES_FILTERS_UPDATED';
const TABLES_TOTAL_UPDATED = 'TABLES_TOTAL_UPDATED';
const LIST_POST_TYPES_FILTERS_UPDATED = 'LIST_POST_TYPES_FILTERS_UPDATED';
const LIST_POST_TYPES_TOTAL_UPDATED = 'LIST_POST_TYPES_TOTAL_UPDATED';
const ENTRIES_UPDATED = 'ENTRIES_UPDATED';
const CRON_JOBS_UPDATED = 'CRON_JOBS_UPDATED';
const INITIALIZED_UPDATED = 'INITIALIZED_UPDATED';
const PLUGINS_UPDATED = 'PLUGINS_UPDATED';
const SELECTED_USEDBY_UPDATED = 'SELECTED_USEDBY_UPDATED';
const TOGGLE_MODAL = 'TOGGLE_MODAL';
const PUSH_BUSY = 'PUSH_BUSY';
const POP_BUSY = 'POP_BUSY';
const PUSH_BUSY_OPTIONS = 'PUSH_BUSY_OPTIONS';
const POP_BUSY_OPTIONS = 'POP_BUSY_OPTIONS';
const PUSH_BUSY_CRON_JOBS = 'PUSH_BUSY_CRON_JOBS';
const POP_BUSY_CRON_JOBS = 'POP_BUSY_CRON_JOBS';
const PUSH_BUSY_BULK_TASKS = 'PUSH_BUSY_BULK_TASKS';
const POP_BUSY_BULK_TASKS = 'POP_BUSY_BULK_TASKS';
const RESET_BUSY_BULK_TASKS = 'RESET_BUSY_BULK_TASKS';
const PUSH_BUSY_CLEAN_BULK_TASKS = 'PUSH_BUSY_CLEAN_BULK_TASKS';
const POP_BUSY_CLEAN_BULK_TASKS = 'POP_BUSY_CLEAN_BULK_TASKS';
const ERROR_UPDATED = 'ERROR_UPDATED';
const SET_DB_SIZES_MUTATE = 'SET_DB_SIZES_MUTATE';
const SET_LIST_POST_TYPES_MUTATE = 'SET_LIST_POST_TYPES_MUTATE';
const SET_POSTS_MUTATE = 'SET_POSTS_MUTATE';
const SET_POSTS_METADATA_MUTATE = 'SET_POSTS_METADATA_MUTATE';
const SET_USERS_MUTATE = 'SET_USERS_MUTATE';
const SET_COMMENTS_MUTATE = 'SET_COMMENTS_MUTATE';
const SET_TRANSIENTS_MUTATE = 'SET_TRANSIENTS_MUTATE';
const SET_OPTIONS_MUTATE = 'SET_OPTIONS_MUTATE';
const SET_SHOULD_FETCH_OPTIONS = 'SET_SHOULD_FETCH_OPTIONS';
const SET_SHOULD_FETCH_CRON_JOBS_OPTIONS = 'SET_SHOULD_FETCH_CRON_JOBS_OPTIONS';
const SET_CRON_JOBS_MUTATE = 'SET_CRON_JOBS_MUTATE';
const SET_PLUGINS_MUTATE = 'SET_PLUGINS_MUTATE';
const LOAD_ENTRY_ITEMS_UPDATED = 'LOAD_ENTRY_ITEMS_UPDATED';
const LOAD_ENTRY_ITEMS_COUNT_UPDATED = 'LOAD_ENTRY_ITEMS_COUNT_UPDATED';
const LOAD_ENTRY_ITEM_UPDATED = 'LOAD_ENTRY_ITEM_UPDATED';
const LOAD_ENTRY_ITEM_COUNT_UPDATED = 'LOAD_ENTRY_ITEM_COUNT_UPDATED';
const LOAD_ENTRY_ITEM_PAGE_UPDATED = 'LOAD_ENTRY_ITEM_PAGE_UPDATED';

/****************************************
  Global reducer
****************************************/

const globalStateReducer = (state, action) => {
  switch (action.type) {

    case ERROR_UPDATED: {
      const { apiErrors } = action;
      return {...state, apiErrors };
    }

    case SET_DB_SIZES_MUTATE: {
      const { mutateDbSizes } = action;
      return {...state, mutateDbSizes };
    }

    case SET_LIST_POST_TYPES_MUTATE: {
      const { mutateListPostTypes } = action;
      return {...state, mutateListPostTypes };
    }

    case SET_POSTS_MUTATE: {
      const { mutatePosts } = action;
      return {...state, mutatePosts };
    }

    case SET_POSTS_METADATA_MUTATE: {
      const { mutatePostsMetadata } = action;
      return {...state, mutatePostsMetadata };
    }

    case SET_USERS_MUTATE: {
      const { mutateUsers } = action;
      return {...state, mutateUsers };
    }

    case SET_COMMENTS_MUTATE: {
      const { mutateComments } = action;
      return {...state, mutateComments };
    }

    case SET_TRANSIENTS_MUTATE: {
      const { mutateTransients } = action;
      return {...state, mutateTransients };
    }

    case SET_OPTIONS_MUTATE: {
      const { mutateOptions } = action;
      return {...state, mutateOptions };
    }

    case SET_CRON_JOBS_MUTATE: {
      const { mutateCronJobs } = action;
      return {...state, mutateCronJobs };
    }

    case SET_PLUGINS_MUTATE: {
      const { mutatePlugins } = action;
      return {...state, mutatePlugins };
    }

    case SET_SHOULD_FETCH_OPTIONS: {
      return {...state, shouldFetchOptions: true };
    }

    case SET_SHOULD_FETCH_CRON_JOBS_OPTIONS: {
      return {...state, shouldFetchCronJobs: true };
    }

    case OPTIONS_FILTERS_UPDATED: {
      const { optionsFilters } = action;
      return {...state, optionsFilters  };
    }

    case OPTIONS_TOTAL_UPDATED: {
      const { options, search } = action;
      const optionsTotal = {};
      optionsFilters.forEach(v => {
        const filtered = filterItems(options, v.value, optionsFilters, false, search);
        const filteredWithHideUsedByWordPress = filterItems(options, v.value, optionsFilters, true, search);
        optionsTotal[v.value] = {
          false: filtered.length,
          true: filteredWithHideUsedByWordPress.length,
        };
      });
      return {...state, optionsTotal };
    }

    case CRON_JOBS_FILTERS_UPDATED: {
      const { cronJobsFilters } = action;
      return {...state, cronJobsFilters };
    }

    case CRON_JOBS_TOTAL_UPDATED: {
      const { cronJobs, search } = action;
      const cronJobsTotal = {};
      cronsFilters.forEach(v => {
        const filtered = filterItems(cronJobs, v.value, cronsFilters, false, search);
        const filteredWithHideUsedByWordPress = filterItems(cronJobs, v.value, cronsFilters, true, search);
        cronJobsTotal[v.value] = {
          false: filtered.length,
          true: filteredWithHideUsedByWordPress.length,
        };
      });
      return {...state, cronJobsTotal };
    }

    case TABLES_TOTAL_UPDATED: {
      const { dbSizes, search } = action;
      const tablesTotal = {};
      tablesFilters.forEach(v => {
        const filtered = filterItems(dbSizes, v.value, tablesFilters, false, search);
        const filteredWithHideUsedByWordPress = filterItems(dbSizes, v.value, tablesFilters, true, search);
        tablesTotal[v.value] = {
          false: filtered.length,
          true: filteredWithHideUsedByWordPress.length,
        };
      });
      return {...state, tablesTotal };
    }

    case TABLES_FILTERS_UPDATED: {
      const { tablesFilters } = action;
      return {...state, tablesFilters };
    }

    case LIST_POST_TYPES_FILTERS_UPDATED: {
      const { listPostTypesFilters } = action;
      return {...state, listPostTypesFilters };
    }

    case LIST_POST_TYPES_TOTAL_UPDATED: {
      const { listPostTypes, search } = action;
      const listPostTypesTotal = {};
      postTypesFilters.forEach(v => {
        const filtered = filterItems(listPostTypes, v.value, postTypesFilters, false, search);
        const filteredWithHideUsedByWordPress = filterItems(listPostTypes, v.value, postTypesFilters, true, search);
        listPostTypesTotal[v.value] = {
          false: filtered.length,
          true: filteredWithHideUsedByWordPress.length,
        };
      });
      return {...state, listPostTypesTotal };
    }

    case TOGGLE_MODAL: {
      let { modal, enable, onNext, title, data } = action;
      if (state.modals[modal] === undefined) {
        alert(`The modal ${modal} doesn't exist.`);
        console.error(`The modal ${modal} doesn't exist.`);
        return state;
      }
      if (enable === undefined) {
        enable = !state.modals[modal].enable;
      }
      if (title === undefined) {
        title = defaultModalTitles[modal];
      }
      if (!enable) {
        title = '';
      }
      const freshState = { ...state };
      freshState.modals = { ...freshState.modals };
      freshState.modals[modal] = { enable, onNext, title, data };
      return freshState;
    }

    case PUSH_BUSY: {
      const { status = '' } = action;
      return { ...state, busy: ++busyCounter > 0, status };
    }

    case POP_BUSY: {
      const { status = '' } = action;
      return { ...state, busy: --busyCounter > 0, status };
    }

    case PUSH_BUSY_OPTIONS: {
      return { ...state, busyOptions: ++busyOptionsCounter > 0};
    }

    case POP_BUSY_OPTIONS: {
      return { ...state, busyOptions: --busyOptionsCounter > 0 };
    }

    case PUSH_BUSY_CRON_JOBS: {
      return { ...state, busyCronJobs: ++busyCronJobsCounter > 0};
    }

    case POP_BUSY_CRON_JOBS: {
      return { ...state, busyCronJobs: --busyCronJobsCounter > 0 };
    }

    case PUSH_BUSY_BULK_TASKS: {
      return { ...state, busyBulkTasks: ++busyBulkTaskCounter > 0 };
    }

    case POP_BUSY_BULK_TASKS: {
      return { ...state, busyBulkTasks: --busyBulkTaskCounter > 0 };
    }

    case RESET_BUSY_BULK_TASKS: {
      return { ...state, busyBulkTasks: 0 };
    }

    case PUSH_BUSY_CLEAN_BULK_TASKS: {
      return { ...state, busyCleanBulkTasks: ++busyCleanBulkTaskCounter > 0 };
    }

    case POP_BUSY_CLEAN_BULK_TASKS: {
      return { ...state, busyCleanBulkTasks: --busyCleanBulkTaskCounter > 0 };
    }

    case STATUS_UPDATED: {
      const { status = '' } = action;
      return { ...state, status };
    }

    case SETTINGS_UPDATED: {
      const { options } = action;
      const agaThreshold = options.aga_threshold;
      const customQueries = options.custom_queries;
      const bulkBatchSize = options.bulk_batch_size;
      const deepDeletions = options.deep_deletions;
      const optionsLimit = Number(options.options_limit);
      const cronJobsLimit = Number(options.cron_jobs_limit);
      const dbSizesLimit = Number(options.db_historical_sizes_limit);
      const listPostTypesLimit = Number(options.list_post_types_limit);
      const postTypesUsedBy = options.post_type_usedby;
      const optionsUsedBy = options.option_usedby;
      const tablesUsedBy = options.table_usedby;
      const cronJobsUsedBy = options.cron_usedby;
      const totalSizes = options.db_historical_sizes;
      const mode = options.mode;
      const hideMessage = options.hide_message;
      const delay = options.delay;
      const totalSize = totalSizes ? totalSizes[totalSizes.length - 1] : null;
      let previousTotalSize = null
      if (totalSizes?.length > 1) {
        previousTotalSize = totalSizes[totalSizes.length - 2];
      } else if (totalSizes?.length > 0) {
        previousTotalSize = totalSizes[totalSizes.length - 1];
      }
      const optionsFilters = { ...state.optionsFilters, limit: optionsLimit };
      const cronJobsFilters = { ...state.cronJobsFilters, limit: cronJobsLimit };
      const tablesFilters = { ...state.tablesFilters, limit: dbSizesLimit };
      const listPostTypesFilters = { ...state.listPostTypesFilters, limit: listPostTypesLimit };
      return { ...state, settingsOptions: options, optionsFilters, cronJobsFilters, tablesFilters,
        listPostTypesFilters, agaThreshold, customQueries, totalSizes,
        bulkBatchSize, deepDeletions, totalSize, previousTotalSize, postTypesUsedBy,
        optionsUsedBy, tablesUsedBy, cronJobsUsedBy, mode, hideMessage, delay };
    }

    case DB_SIZES_UPDATED: {
      const { dbSizes } = action;
      return { ...state, dbSizes };
    }

    case LIST_POST_TYPES_UPDATED: {
      const { listPostTypes } = action;
      return { ...state, listPostTypes };
    }

    case LIST_POST_TYPES_COUNT_UPDATED: {
      const { listPostTypesCount } = action;
      return { ...state, listPostTypesCount };
    }

    case POSTS_UPDATED: {
      const { posts } = action;
      return { ...state, posts };
    }

    case POSTS_COUNT_UPDATED: {
      const { postsCount } = action;
      return { ...state, postsCount };
    }

    case POSTS_METADATA_UPDATED: {
      const { postsMetadata } = action;
      return { ...state, postsMetadata };
    }

    case POSTS_METADATA_COUNT_UPDATED: {
      const { postsMetadataCount } = action;
      return { ...state, postsMetadataCount };
    }

    case USERS_UPDATED: {
      const { users } = action;
      return { ...state, users };
    }

    case USERS_COUNT_UPDATED: {
      const { usersCount } = action;
      return { ...state, usersCount };
    }

    case COMMENTS_UPDATED: {
      const { comments } = action;
      return { ...state, comments };
    }

    case COMMENTS_COUNT_UPDATED: {
      const { commentsCount } = action;
      return { ...state, commentsCount };
    }

    case TRANSIENTS_UPDATED: {
      const { transients } = action;
      return { ...state, transients };
    }

    case TRANSIENTS_COUNT_UPDATED: {
      const { transientsCount } = action;
      return { ...state, transientsCount };
    }

    case OPTIONS_UPDATED: {
      const { options } = action;
      return { ...state, options };
    }

    case FILTERED_OPTIONS_UPDATED: {
      const { options, filterBy, limit, page, hideUsedByWordPress, search, accessor, sort } = action;
      const ignoreOptionNames = ['cron'];
      const sorted = sortItems(options, accessor, sort);
      const list = sorted.filter(v => !ignoreOptionNames.includes(v.option_name));
      const newFiltered = paging(filterItems(list, filterBy, optionsFilters, hideUsedByWordPress, search), page, limit);
      return { ...state, filteredOptions: newFiltered };
    }

    case FILTERED_CRON_JOBS_UPDATED: {
      const { cronJobs, filterBy, limit, page, hideUsedByWordPress, search, accessor, sort } = action;
      const sorted = sortItems(cronJobs, accessor, sort);
      const newFiltered = paging(filterItems(sorted, filterBy, cronsFilters, hideUsedByWordPress, search), page, limit);
      return { ...state, filteredCronJobs: newFiltered };
    }

    case FILTERED_POST_TYPES_UPDATED: {
      const { listPostTypes, filterBy, limit, page, hideUsedByWordPress, search, accessor, sort } = action;
      const sorted = sortItems(listPostTypes, accessor, sort);
      const newFiltered = paging(filterItems(sorted, filterBy, postTypesFilters, hideUsedByWordPress, search), page, limit);
      return { ...state, filteredListPostTypes: newFiltered };
    }

    case FILTERED_TABLES_UPDATED: {
      const { dbSizes, filterBy, limit, page, hideUsedByWordPress, search, accessor, sort } = action;
      const sorted = sortItems(dbSizes, accessor, sort);
      const newFiltered = paging(filterItems(sorted, filterBy, tablesFilters, hideUsedByWordPress, search), page, limit).map(v => {
        return {
          ...v,
          tableName: removeDbPrefix(v.table)
        }
      });
      return { ...state, filteredTables: newFiltered };
    }

    case CUSTOM_QUERIES_COUNT_UPDATED: {
      const { customQueriesCount } = action;
      return { ...state, customQueriesCount };
    }

    case EDIT_CUSTOM_QUERIES_UPDATED: {
      const { editCustomQueries } = action;
      return { ...state, editCustomQueries };
    }

    case CLICKED_TAB_UPDATED: {
      const { clickedTab } = action;
      return { ...state, clickedTab };
    }

    case FILE_CONTENT_UPDATED: {
      const { fileContent } = action;
      return { ...state, fileContent };
    }

    case BUSY_ACTION_UPDATED: {
      const { busyAction } = action;
      return { ...state, busyAction };
    }

    case ENTRIES_UPDATED: {
      const { entries } = action;
      const entriesTableColumns = entries.length ? 
        Object.keys(entries.at(0)).filter(o => viewableSQLColumns.includes(o)).map(o => {
          const title = o.replace(/_/g, ' ');
          const cleanTitle = title.replace(/^post/, '').replace(/\w\S*/g, (w) => (w.replace(/^\w/, (c) => c.toUpperCase())));
          return { accessor: o, title: cleanTitle, sortable: false, style: { width: 'auto', maxWidth: '400px', minWidth: '50px' } };
        }) : {};
      return { ...state, entries, entriesTableColumns };
    }

    case CRON_JOBS_UPDATED: {
      const { cronJobs } = action;
      return {...state, cronJobs };
    }

    case INITIALIZED_UPDATED: {
      const { tab, value } = action;
      const newInitialized = {...state.initialized};
      newInitialized[tab] = value;
      return {...state, initialized: newInitialized };
    }

    case PLUGINS_UPDATED: {
      const { plugins } = action;
      return {...state, plugins };
    }

    case SELECTED_USEDBY_UPDATED: {
      const { selectedUsedBy } = action;
      return {...state, selectedUsedBy };
    }

    case LOAD_ENTRY_ITEMS_UPDATED: {
      const { key, data, itemKey = 'item' } = action;
      const defaultCount = [...coreKeys, loadEntryItemKeys.listPostTypes].includes(key) ? undefined : true;
      const coreCountKey = camelToSnake(key);
      const initialCounts = coreCount.hasOwnProperty(coreCountKey) ? coreCount[coreCountKey] : [];
      const newEntryItemsOfKey = sortIgnoreItemToEnd(data).map(v => {
        return {
          item: v[itemKey],
          count: initialCounts.find(initialCount => initialCount.item === v[itemKey])?.count ?? defaultCount
        }
      });
      return {...state, loadEntryItems: {...state.loadEntryItems, [key]: newEntryItemsOfKey} };
    }

    case LOAD_ENTRY_ITEMS_COUNT_UPDATED: {
      const { item, count } = action;
      const key = Object.keys(state.loadEntryItems).find(k => state.loadEntryItems[k].some(v => v.item === item));
      if (!key) {
        return {...state};
      }

      const newEntryItemsOfKey = state.loadEntryItems[key].map(v => {
        return (v.item === item) ? { ...v, count } : v;
      })
      return {...state, loadEntryItems: {...state.loadEntryItems, [key]: newEntryItemsOfKey} };
    }

    case LOAD_ENTRY_ITEM_UPDATED: {
      const { loadEntryItem } = action;

      const currentItem = loadEntryItem?.currentItem;
      const loadEntryPrevItem = currentItem ? getNextOrPrevLoadEntryItem(currentItem, state.loadEntryItems, 'prev') : null;
      const loadEntryNextItem = currentItem ? getNextOrPrevLoadEntryItem(currentItem, state.loadEntryItems, 'next') : null;

      return {...state, loadEntryItem, loadEntryPrevItem, loadEntryNextItem };
    }

    case LOAD_ENTRY_ITEM_COUNT_UPDATED: {
      const { count } = action;
      return {...state, loadEntryItemCount: count };
    }

    case LOAD_ENTRY_ITEM_PAGE_UPDATED: {
      const { page } = action;
      return {...state, loadEntryItemPage: page };
    }

    default:
      return state;
  }
};

/****************************************
  Global state
****************************************/

const DatabaseCleanerContext = createContext();

const useDatabaseCleanerContext = () => {
  const actions = {};
  const [state, dispatch] = useContext(DatabaseCleanerContext);

  // Update Data
  actions.updateOption = async ({ busyItem, param, onSuccess, onFailure }) => {
    if (busyItem) {
      dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, { item : busyItem }] });
    }

    const newOptions = {
      ...state.settingsOptions,
      [param.name]: param.value
    };
    try {
      const response = await postFetch(`${API}/update_options`, {
        json: {
          options: newOptions
        },
        nonce: restNonce
      });
      if (response.success) {
        dispatch({ type: SETTINGS_UPDATED, options: response.options });
        if (onSuccess) onSuccess();
      }
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
      onFailure(err);
    }
    finally {
      if (busyItem) {
        dispatch({ type: BUSY_ACTION_UPDATED, busyAction: state.busyAction.filter(v => v.item !== busyItem) });
      }
    }
  }
  actions.resetOptions = async () => {
    dispatch({ type: PUSH_BUSY });
    try {
      const response = await postFetch(`${API}/reset_options`, { nonce: restNonce });
      if (response.success) {
        dispatch({ type: SETTINGS_UPDATED, options: response.data });
        await actions.mutateResetAll();
      }
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    dispatch({ type: POP_BUSY });
  }

  actions.mutateResetAll = async () => {
    await state.mutateDbSizes();
    await state.mutatePosts();
    await state.mutatePostsMetadata();
    await state.mutateUsers();
    await state.mutateComments();
    await state.mutateListPostTypes();
    await state.mutateTransients();
    await state.mutateOptions();
    dispatch({ type: LIST_POST_TYPES_COUNT_UPDATED, listPostTypesCount: [] });
    dispatch({ type: POSTS_COUNT_UPDATED, postsCount: [] });
    dispatch({ type: POSTS_METADATA_COUNT_UPDATED, postsCount: [] });
    dispatch({ type: USERS_COUNT_UPDATED, usersCount: [] });
    dispatch({ type: COMMENTS_COUNT_UPDATED, commentsCount: [] });
    dispatch({ type: TRANSIENTS_COUNT_UPDATED, transientsCount: [] });
    dispatch({ type: CUSTOM_QUERIES_COUNT_UPDATED, customQueriesCount: [] });
    dispatch({ type: EDIT_CUSTOM_QUERIES_UPDATED, editCustomQueries: [] });
    dispatch({ type: CLICKED_TAB_UPDATED, clickedTab: defaultClickedTab });
  }

  // Entries
  actions.deleteEntries = async ({ item, isAutoClean = false, onSuccess, signal }) => {
    try {
      const response = await postFetch(`${API}/delete_entries`, { json: { item, is_auto_clean: isAutoClean }, signal, nonce: restNonce });
      if (response.success && onSuccess) onSuccess();
      return response;
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
  }
  actions.getEntries = async ({ item, offset = 0 }) => {
    const controller = new AbortController();
    const signal = controller.signal;
    const url = buildUrlWithParams(`${API}/entries/`, { item, offset });

    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, { item: `loading_${item}` }] });
    try {
      const response = await getFetch( url, null, signal, restNonce );
      if (!response.success) {
        if (response.message) alert(response.message);
        return;
      }
      await dispatch({ type: ENTRIES_UPDATED, entries: response.data });
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    finally {
      dispatch({ type: BUSY_ACTION_UPDATED, busyAction: state.busyAction.filter(v => v.item !== `loading_${item}`) });
    }
  }

  // Option
  actions.getOptionValue = async ({ optionName }) => {
    const controller = new AbortController();
    const signal = controller.signal;
    const url = buildUrlWithParams(`${API}/option_value/`, { option_name: optionName });

    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, { item: `loading_${optionName}` }] });
    try {
      const response = await getFetch( url, null, signal, restNonce );
      if (!response.success) {
        if (response.message) alert(response.message);
        return;
      }
      dispatch({ type: ENTRIES_UPDATED, entries: response.data });
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    finally {
      dispatch({ type: BUSY_ACTION_UPDATED, busyAction: state.busyAction.filter(v => v.item !== `loading_${optionName}`) });
    }
  }

  // Custom Queries
  actions.customQueryDelete = async ({ item, onSuccess, signal }) => {
    try {
      const response = await postFetch(`${API}/custom_query_delete`, { json: { item }, signal, nonce: restNonce });
      if (!response.success && response.message) {
        alert(`Error: ${response.message}`);
      } else if (onSuccess) {
        onSuccess(item, response);
      };
      return response;
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
  }
  actions.refreshCostomQueryCount = async ({ item, onSuccess }) => {
    const controller = new AbortController();
    const signal = controller.signal;
    const url = buildUrlWithParams(`${API}/custom_query_count/`, { item });

    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, { item: countBusyValue(item) }] });
    try {
      const response = await getFetch( url, null, signal, restNonce );
      if (!response.success) {
        if (response.message) alert(response.message);
        return;
      }
      if (onSuccess) {
        onSuccess(item, response);
      }
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    finally {
      dispatch({ type: BUSY_ACTION_UPDATED, busyAction: state.busyAction.filter(v => v.item !== countBusyValue(item)) });
    }
  }
  actions.allRefreshCostomQueryCount = async ({ item, onSuccess, counts }) => {
    const controller = new AbortController();
    const signal = controller.signal;
    const url = buildUrlWithParams(`${API}/custom_query_count/`, { item });
    let newCounts = [...counts];

    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, { item: countBusyValue(item) }] });
    try {
      const response = await getFetch( url, null, signal, restNonce );
      if (!response.success) {
        if (response.message) alert(response.message);
        return;
      }
      newCounts = [...counts.filter(v => v.item !== item), { item, count: response.data }];
      onSuccess(newCounts);
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    finally {
      dispatch({ type: BUSY_ACTION_UPDATED, busyAction: state.busyAction.filter(v => v.item !== countBusyValue(item)) });
    }
    return newCounts;
  }

  // Count
  actions.loadEntryCount = async ({ item, onSuccess }) => {
    const controller = new AbortController();
    const signal = controller.signal;
    const url = buildUrlWithParams(`${API}/entry_count/`, { item });

    try {
      const response = await getFetch( url, null, signal, restNonce );
      if (!response.success) {
        if (response.message) alert(response.message);
        return;
      }
      if (onSuccess) {
        onSuccess(item, response);
      }
      dispatch({ type: LOAD_ENTRY_ITEMS_COUNT_UPDATED, item, count: response.data });
      return response.data;
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
  }
  actions.refreshEntryCount = async ({ busyItem, item, onSuccess }) => {
    const controller = new AbortController();
    const signal = controller.signal;
    const url = buildUrlWithParams(`${API}/entry_count/`, { item });

    const busyActionItem = busyItem ?? countBusyValue(item);
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, { item: busyActionItem }] });
    try {
      const response = await getFetch( url, null, signal, restNonce );
      if (!response.success) {
        if (response.message) alert(response.message);
        return;
      }
      if (onSuccess) {
        onSuccess(item, response);
      }
      dispatch({ type: LOAD_ENTRY_ITEMS_COUNT_UPDATED, item, count: response.data });
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    finally {
      dispatch({ type: BUSY_ACTION_UPDATED, busyAction: state.busyAction.filter(v => v.item !== busyActionItem) });
    }
  }

  actions.allRefreshEntryCount = async ({ item, onSuccess, counts }) => {
    const controller = new AbortController();
    const signal = controller.signal;
    const url = buildUrlWithParams(`${API}/entry_count/`, { item });
    let newCounts = [...counts];

    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, { item: countBusyValue(item) }] });
    try {
      const response = await getFetch( url, null, signal, restNonce );
      if (!response.success) {
        if (response.message) alert(response.message);
        return;
      }
      newCounts = [...counts.filter(v => v.item !== item), { item, count: response.data }];
      dispatch({ type: LOAD_ENTRY_ITEMS_COUNT_UPDATED, item, count: response.data });
      onSuccess(newCounts);
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    finally {
      dispatch({ type: BUSY_ACTION_UPDATED, busyAction: state.busyAction.filter(v => v.item !== countBusyValue(item)) })
      return newCounts;
    }
  };

  // Option
  actions.deleteOption = async ({ item }) => {
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, { item }] });
    try {
      const response = await postFetch(`${API}/delete_options`, { json: { item }, nonce: restNonce });
      if (!response.success) {
        if (response.message) alert(response.message);
        return;
      }
      await takeNap(state.delay);
      state.mutateOptions();
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    finally {
      dispatch({ type: BUSY_ACTION_UPDATED, busyAction: state.busyAction.filter(v => v.item !== item) });
    }
  }
  actions.deleteOptions = async ({ items }) => {
    const newBusyActions = items.map(v => { return { item: v } });
    const currentBusyAction = [...state.busyAction];
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, ...newBusyActions] });
    let data = [];
    try {
      const response = await postFetch(`${API}/delete_options`, { json: { items }, nonce: restNonce });
      if (!response.success) {
        if (response.message) alert(response.message);
        data = response.data ? [...response.data] : [];
      }
      await takeNap(state.delay);
      state.mutateOptions();
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...currentBusyAction] });
    return data;
  }
  
  actions.switchAutoloadedOption = async ({ item, autoload }) => {
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, { item }] });
    try {
      const response = await postFetch(`${API}/switch_autoloaded_option`, { json: { item, autoload }, nonce: restNonce });
      if (!response.success) return;
      state.mutateOptions();
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    finally {
      dispatch({ type: BUSY_ACTION_UPDATED, busyAction: state.busyAction.filter(v => v.item !== item) });
    }
  }

  // Cron Jobs
  actions.deleteCron = async ({ item }) => {
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, { item: item.name }] });
    try {
      const response = await postFetch(`${API}/delete_crons`, { json: { item }, nonce: restNonce });
      if (!response.success) {
        if (response.message) alert(response.message);
        return;
      }
      await takeNap(state.delay);
      state.mutateCronJobs();
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    finally {
      dispatch({ type: BUSY_ACTION_UPDATED, busyAction: state.busyAction.filter(v => v.item !== item) });
    }
  }
  actions.deleteCrons = async ({ items }) => {
    const newBusyActions = items.map(v => { return { item: v.name } });
    const currentBusyAction = [...state.busyAction];
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, ...newBusyActions] });
    let data = [];
    try {
      const response = await postFetch(`${API}/delete_crons`, { json: { items }, nonce: restNonce });
      if (!response.success) {
        if (response.message) alert(response.message);
        data = [...response.data];
      }
      await takeNap(state.delay);
      state.mutateCronJobs();
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...currentBusyAction] });
    return data;
  }

  // Tab
  actions.updateClickedTabByIndex = (tabTitle) => {
    let clickedTab = {...state.clickedTab};
    const clickedTabKey = tabTitle.replace(/\s+/g, '');
    const clickedCount = state.clickedTab[clickedTabs[clickedTabKey]] + 1
    clickedTab[clickedTabs[clickedTabKey]] = clickedCount > 1 ? 2 : clickedCount;
    dispatch({ type: CLICKED_TAB_UPDATED, clickedTab });
  }

  actions.updateClickedTabByTitle = (tabTitle) => {
    let clickedTab = {...state.clickedTab};
    const clickedTabKey = tabTitle.replace(/\s+/g, '');
    clickedTab[clickedTabKey] = 1;
    dispatch({ type: CLICKED_TAB_UPDATED, clickedTab });
  }

  // Logs Actions
  actions.logDbSize = async () => {
    await postFetch(`${API}/log_db_size`, { nonce: restNonce });
  }
  actions.refreshLogs = async () => {
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, { item: 'logs' }] });
    const res = await postFetch(`${API}/refresh_logs`, { nonce: restNonce });
    if (res.success) {
      dispatch({ type: FILE_CONTENT_UPDATED, fileContent: res.data });
    }
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction] });
  }
  actions.clearLogs = async () => {
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, { item: 'logs' }] });
    const res = await postFetch(`${API}/clear_logs`, { nonce: restNonce });
    if (res.success) {
      dispatch({ type: FILE_CONTENT_UPDATED, fileContent: res.data });
    }
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction] });
  }

  // Generate Fake Data
  actions.generateFakeData = async () => {
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, { item: 'fakeData' }] });
    await postFetch(`${API}/generate_fake_data`, { nonce: restNonce });
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction] });
  }

  // Custom Used By
  actions.deleteCustomUsedBy = async () => {
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, { item: 'deleteCustomUsedBy' }] });
    const newOptions = {
      ...state.settingsOptions,
      post_type_usedby: [],
      option_usedby: [],
      table_usedby: [],
      cron_usedby: [],
    };
    try {
      const response = await postFetch(`${API}/update_options`, {
        json: {
          options: newOptions
        },
        nonce: restNonce
      });
      if (response.success) {
        dispatch({ type: SETTINGS_UPDATED, options: response.options });
      }
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
      onFailure(err);
    }
    finally {
      dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction] });
    }
  }

  // Tables
  actions.deleteTables = async ({ tables }) => {
    const newBusyActions = tables.map(v => { return { item: `delete_${v}` } });
    const currentBusyAction = [...state.busyAction];
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, ...newBusyActions] });
    let data = [];
    try {
      const response = await postFetch(`${API}/delete_tables`, { json: { tables: tables.map(v => addDbPrefix(v)) }, nonce: restNonce });
      if (!response.success) {
        if (response.message) alert(response.message);
        data = response.data ? [...response.data].map(v => removeDbPrefix(v)) : [];
      }
      await takeNap(state.delay);
      state.mutateDbSizes();
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...currentBusyAction] });
    return data;
  }
  actions.optimizeTables = async ({ tables }) => {
    const newBusyActions = tables.map(v => { return { item: `optimize_${v}` } });
    const currentBusyAction = [...state.busyAction];
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, ...newBusyActions] });
    let data = [];
    try {
      const response = await postFetch(`${API}/optimize_tables`, { json: { tables: tables.map(v => addDbPrefix(v)) }, nonce: restNonce });
      if (!response.success) {
        if (response.message) alert(response.message);
        data = response.data ? [...response.data].map(v => removeDbPrefix(v)) : [];
      }
      state.mutateDbSizes();
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...currentBusyAction] });
    return data;
  }
  actions.repairTables = async ({ tables }) => {
    const newBusyActions = tables.map(v => { return { item: `repair_${v}` } });
    const currentBusyAction = [...state.busyAction];
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, ...newBusyActions] });
    let data = [];
    try {
      const response = await postFetch(`${API}/repair_tables`, { json: { tables: tables.map(v => addDbPrefix(v)) }, nonce: restNonce });
      if (!response.success) {
        if (response.message) alert(response.message);
        data = response.data ? [...response.data].map(v => removeDbPrefix(v)) : [];
      }
      state.mutateDbSizes();
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...currentBusyAction] });
    return data;
  }

  // Total DB sizes
  actions.getTotalDbSizes = async() => {
    const controller = new AbortController();
    const signal = controller.signal;
    dispatch({ type: BUSY_ACTION_UPDATED, busyAction: [...state.busyAction, { item: totalDbSizeKey }] });
    try {
      const response = await getFetch(`${API}/total_db_size/`, null, signal, restNonce );
      if (!response.success) {
        if (response.message) alert(response.message);
        return;
      }
      console.log('Historical DB Sizes', response.data.db_historical_sizes);
      dispatch({ type: SETTINGS_UPDATED, options: response.data });
      //await takeNap(state.delay);
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
    finally {
      dispatch({ type: BUSY_ACTION_UPDATED, busyAction: state.busyAction.filter(v => v.item !== totalDbSizeKey) });
    }
  }

  // Auto Clean
  actions.getAutoCleanItems = async() => {
    const controller = new AbortController();
    const signal = controller.signal;
    try {
      const response = await getFetch(`${API}/auto_clean_items/`, null, signal, restNonce );
      return response.data;
    }
    catch (err) {
      if (err.message) {
        alert(err.message);
      }
    }
  }
  actions.resetAutoCleanItemsCount = (items) => {
    const getNewCounts = (currentCounts, targetItems) => {
      return currentCounts.map(v => {
        return {
          ...v,
          count: targetItems.includes(v.item) ? 0 : v.count
        };
      });
    }
    // Posts
    const postsItems = items.filter(v => v.item.includes('posts_') && !v.item.includes('posts_metadata_')).map(v => v.item);
    const newPostsCount = getNewCounts(state.postsCount, postsItems);
    dispatch({ type: POSTS_COUNT_UPDATED, postsCount: newPostsCount });

    // Posts Metadata
    const postsMetadataItems = items.filter(v => v.item.includes('posts_metadata_')).map(v => v.item);
    const newPostsMetadataCount = getNewCounts(state.postsMetadataCount, postsMetadataItems);
    dispatch({ type: POSTS_METADATA_COUNT_UPDATED, postsMetadataCount: newPostsMetadataCount });

    // Users
    const usersItems = items.filter(v => v.item.includes('users_')).map(v => v.item);
    const newUsersCount = getNewCounts(state.usersCount, usersItems);
    dispatch({ type: USERS_COUNT_UPDATED, usersCount: newUsersCount });

    // Comments
    const commentsItems = items.filter(v => v.item.includes('comments_')).map(v => v.item);
    const newCommentsCount = getNewCounts(state.commentsCount, commentsItems);
    dispatch({ type: COMMENTS_COUNT_UPDATED, commentsCount: newCommentsCount });

    // Transients
    const transientsItems = items.filter(v => v.item.includes('options_')).map(v => v.item);
    const newTransientsCount = getNewCounts(state.transientsCount, transientsItems);
    dispatch({ type: TRANSIENTS_COUNT_UPDATED, transientsCount: newTransientsCount });

    // Post Types
    const listPostTypesItems = items.filter(v => v.item.includes('list_post_types_')).map(v => v.item);
    const newListPostTypesCount = getNewCounts(state.listPostTypesCount, listPostTypesItems);
    dispatch({ type: LIST_POST_TYPES_COUNT_UPDATED, listPostTypesCount: newListPostTypesCount });
  }

  // Update counts
  actions.setListPostTypesCount = (listPostTypesCount) => { dispatch({ type: LIST_POST_TYPES_COUNT_UPDATED, listPostTypesCount }); };
  actions.setPostsCount = (postsCount) => { dispatch({ type: POSTS_COUNT_UPDATED, postsCount }); };
  actions.setPostsMetadataCount = (postsMetadataCount) => { dispatch({ type: POSTS_METADATA_COUNT_UPDATED, postsMetadataCount }); };
  actions.setUsersCount = (usersCount) => { dispatch({ type: USERS_COUNT_UPDATED, usersCount }); };
  actions.setCommentsCount = (commentsCount) => { dispatch({ type: COMMENTS_COUNT_UPDATED, commentsCount }); };
  actions.setTransientsCount = (transientsCount) => { dispatch({ type: TRANSIENTS_COUNT_UPDATED, transientsCount }); };
  actions.setCustomQueriesCount = (customQueriesCount) => { dispatch({ type: CUSTOM_QUERIES_COUNT_UPDATED, customQueriesCount }); };
  actions.setEditCustomQueries = (editCustomQueries) => { dispatch({ type: EDIT_CUSTOM_QUERIES_UPDATED, editCustomQueries }); };
  actions.setLoadEntryItem = async ({ loadEntryItem, count = 0, page = 1 }) => {
    dispatch({ type: LOAD_ENTRY_ITEM_UPDATED, loadEntryItem });
    dispatch({ type: LOAD_ENTRY_ITEM_COUNT_UPDATED, count });
    dispatch({ type: LOAD_ENTRY_ITEM_PAGE_UPDATED, page });

    if (loadEntryItem == null) return;

    await actions.getEntries({ item: loadEntryItem, offset: state.loadEntryItemLimit * (page - 1) });
  };
  actions.setLoadEntryItemPage = async (page) => {
    dispatch({ type: LOAD_ENTRY_ITEM_PAGE_UPDATED, page });
    await actions.getEntries({ item: state.loadEntryItem, offset: state.loadEntryItemLimit * (page - 1) });
  };

  actions.setBusyAction = (busyAction) => { dispatch({ type: BUSY_ACTION_UPDATED, busyAction }); };
  actions.setShouldFetchOptions = () => { dispatch({ type: SET_SHOULD_FETCH_OPTIONS }); };
  actions.setEntries = (entries) => { dispatch({ type: ENTRIES_UPDATED, entries }); };
  actions.setShouldFetchCronJobs = () => { dispatch({ type: SET_SHOULD_FETCH_CRON_JOBS_OPTIONS }); };
  actions.setInitialized = (tab, value) => { dispatch({ type: INITIALIZED_UPDATED, tab, value }); };
  actions.setSelectedUsedBy = (selectedUsedBy) => { dispatch({ type: SELECTED_USEDBY_UPDATED, selectedUsedBy }); };
  actions.updateOptionsFilters = ({ limit, page, filterBy, hideUsedByWordPress, search, accessor, sort }) => {
    const newLimit = limit ?? state.optionsFilters.limit;
    const newPage = page ?? state.optionsFilters.page;
    const newFilterBy = filterBy ?? state.optionsFilters.filterBy;
    const newHideUsedByWordPress = hideUsedByWordPress ?? state.optionsFilters.hideUsedByWordPress;
    const newSearch = search ?? state.optionsFilters.search;
    const newAccessor = accessor ?? state.optionsFilters.accessor;
    const newSort = sort ?? state.optionsFilters.sort;
    dispatch({ type: OPTIONS_FILTERS_UPDATED, optionsFilters: { ...state.optionsFilters, limit: newLimit, page: newPage, filterBy: newFilterBy,
      hideUsedByWordPress: newHideUsedByWordPress, search: newSearch, accessor: newAccessor, sort: newSort } });
  };
  actions.updateCronJobsFilters = ({ limit, page, filterBy, hideUsedByWordPress, search, accessor, sort }) => {
    const newLimit = limit ?? state.cronJobsFilters.limit;
    const newPage = page ?? state.cronJobsFilters.page;
    const newFilterBy = filterBy ?? state.cronJobsFilters.filterBy;
    const newHideUsedByWordPress = hideUsedByWordPress ?? state.cronJobsFilters.hideUsedByWordPress;
    const newSearch = search ?? state.cronJobsFilters.search;
    const newAccessor = accessor ?? state.cronJobsFilters.accessor;
    const newSort = sort ?? state.cronJobsFilters.sort;
    dispatch({ type: CRON_JOBS_FILTERS_UPDATED, cronJobsFilters: { ...state.cronJobsFilters, limit: newLimit, page: newPage, filterBy: newFilterBy,
      hideUsedByWordPress: newHideUsedByWordPress, search: newSearch, accessor: newAccessor, sort: newSort } });
  };
  actions.updateTablesFilters = ({ limit, page, filterBy, hideUsedByWordPress, search, accessor, sort }) => {
    const newLimit = limit ?? state.tablesFilters.limit;
    const newPage = page ?? state.tablesFilters.page;
    const newFilterBy = filterBy ?? state.tablesFilters.filterBy;
    const newHideUsedByWordPress = hideUsedByWordPress ?? state.tablesFilters.hideUsedByWordPress;
    const newSearch = search ?? state.tablesFilters.search;
    const newAccessor = accessor ?? state.tablesFilters.accessor;
    const newSort = sort ?? state.tablesFilters.sort;
    dispatch({ type: TABLES_FILTERS_UPDATED, tablesFilters: { ...state.tablesFilters, limit: newLimit, page: newPage, filterBy: newFilterBy,
      hideUsedByWordPress: newHideUsedByWordPress, search: newSearch, accessor: newAccessor, sort: newSort } });
  };
  actions.updateListPostTypesFilters = ({ limit, page, filterBy, hideUsedByWordPress, search, accessor, sort }) => {
    const newLimit = limit ?? state.listPostTypesFilters.limit;
    const newPage = page ?? state.listPostTypesFilters.page;
    const newFilterBy = filterBy ?? state.listPostTypesFilters.filterBy;
    const newHideUsedByWordPress = hideUsedByWordPress ?? state.listPostTypesFilters.hideUsedByWordPress;
    const newSearch = search ?? state.listPostTypesFilters.search;
    const newAccessor = accessor ?? state.listPostTypesFilters.accessor;
    const newSort = sort ?? state.listPostTypesFilters.sort;
    dispatch({ type: LIST_POST_TYPES_FILTERS_UPDATED, listPostTypesFilters: { ...state.listPostTypesFilters, limit: newLimit, filterBy: newFilterBy,
      page: newPage, hideUsedByWordPress: newHideUsedByWordPress, search: newSearch, accessor: newAccessor, sort: newSort } });
  };

  // UI Actions
  actions.toggleModal = ({modal, enable = undefined, onNext = () => {}, title, data = {}}) => { dispatch({ type: TOGGLE_MODAL, modal, enable, onNext, title, data }); };
  actions.pushBusy = () => { dispatch({ type: PUSH_BUSY }); };
  actions.popBusy = () => { dispatch({ type: POP_BUSY }); };
  actions.pushBusyBulkTasks = () => { dispatch({ type: PUSH_BUSY_BULK_TASKS }); };
  actions.popBusyBulkTasks = () => { dispatch({ type: POP_BUSY_BULK_TASKS }); };
  actions.resetBusyBulkTasks = () => { dispatch({ type: RESET_BUSY_BULK_TASKS }); };
  actions.pushBusyCleanBulkTasks = () => { dispatch({ type: PUSH_BUSY_CLEAN_BULK_TASKS }); };
  actions.popBusyCleanBulkTasks = () => { dispatch({ type: POP_BUSY_CLEAN_BULK_TASKS }); };
  actions.pushBusyOptions = () => { dispatch({ type: PUSH_BUSY_OPTIONS }); };
  actions.popBusyOptions = () => { dispatch({ type: POP_BUSY_OPTIONS }); };
  actions.pushBusyCronJobs = () => { dispatch({ type: PUSH_BUSY_CRON_JOBS }); };
  actions.popBusyCronJobs = () => { dispatch({ type: POP_BUSY_CRON_JOBS }); };

  return { ...state, ...actions };
};

/****************************************
  Global state provider
****************************************/

export const DatabaseCleanerContextProvider = ({ children }) => {
  const [state, dispatch] = useReducer( globalStateReducer, initialState );

  // DB Sizes
  const { tablesFilters } = state;
  const swrDbSizesKey = useMemo( () => {
    return [ `${API}/db_sizes/`, { headers: { 'X-WP-Nonce': restNonce } } ];
  }, [API, restNonce] );
  const { data: swrDbSizes, mutate: mutateDbSizes } = useSWR(swrDbSizesKey, jsonFetcher);
  const { busy: busyDbSizes, data: dbSizes, error: dbSizesError } = useHandleSWR(swrDbSizes, [], true);
  useEffect(() => { dispatch({ type: SET_DB_SIZES_MUTATE, mutateDbSizes }) }, [mutateDbSizes]);
  useEffect(() => { dispatch({ type: busyDbSizes ? PUSH_BUSY : POP_BUSY }) }, [busyDbSizes]);
  useEffect(() => { dispatch({ type: DB_SIZES_UPDATED, dbSizes }); }, [dbSizes]);
  useEffect(() => { dispatch({ type: TABLES_TOTAL_UPDATED, dbSizes, search: tablesFilters.search }); }, [dbSizes, tablesFilters.search]);
  useEffect(() => { dispatch({ type: ERROR_UPDATED, apiError: dbSizesError }); }, [dbSizesError]);
  useEffect(() => { dispatch({
    type: FILTERED_TABLES_UPDATED,
    dbSizes, filterBy: tablesFilters.filterBy, limit: tablesFilters.limit, page: tablesFilters.page,
    hideUsedByWordPress: tablesFilters.hideUsedByWordPress, search: tablesFilters.search,
    accessor: tablesFilters.accessor, sort: tablesFilters.sort});
  },
    [dbSizes, tablesFilters.filterBy, tablesFilters.page, tablesFilters.limit, tablesFilters.hideUsedByWordPress,
      tablesFilters.search, tablesFilters.accessor, tablesFilters.sort]
  );

  // Posts Types
  const { listPostTypesFilters } = state;
  const swrListPostTypesKey = useMemo( () => {
    return [ `${API}/list_post_types/`, { headers: { 'X-WP-Nonce': restNonce } } ];
  }, [ API, restNonce ] );
  const { data: swrListPostTypes, mutate: mutateListPostTypes } = useSWR(swrListPostTypesKey, jsonFetcher);
  const { busy: busyListPostTypes, data: listPostTypes, error: listPostTypesError } = useHandleSWR(swrListPostTypes, [], true);
  useEffect(() => { dispatch({ type: SET_LIST_POST_TYPES_MUTATE, mutateListPostTypes }) }, [mutateListPostTypes]);
  useEffect(() => { dispatch({ type: busyListPostTypes ? PUSH_BUSY : POP_BUSY }) }, [busyListPostTypes]);
  useEffect(() => { dispatch({ type: LIST_POST_TYPES_UPDATED, listPostTypes }); }, [listPostTypes]);
  useEffect(() => { dispatch({ type: LOAD_ENTRY_ITEMS_UPDATED, key: loadEntryItemKeys.listPostTypes, data: listPostTypes }); }, [listPostTypes]);
  useEffect(() => { dispatch({ type: LIST_POST_TYPES_TOTAL_UPDATED, listPostTypes, search: listPostTypesFilters.search }); }, [listPostTypes, listPostTypesFilters.search]);
  useEffect(() => { dispatch({ type: ERROR_UPDATED, apiError: listPostTypesError }); }, [listPostTypesError]);
  useEffect(() => { dispatch({
    type: FILTERED_POST_TYPES_UPDATED,
    listPostTypes, filterBy: listPostTypesFilters.filterBy, limit: listPostTypesFilters.limit, page: listPostTypesFilters.page,
    hideUsedByWordPress: listPostTypesFilters.hideUsedByWordPress, search: listPostTypesFilters.search,
    accessor: listPostTypesFilters.accessor, sort: listPostTypesFilters.sort});
  },
    [listPostTypes, listPostTypesFilters.filterBy, listPostTypesFilters.page, listPostTypesFilters.limit,
      listPostTypesFilters.hideUsedByWordPress, listPostTypesFilters.search, listPostTypesFilters.accessor, listPostTypesFilters.sort]
  );

  // Posts
  const swrPostsKey = useMemo( () => {
    return [ `${API}/posts/`, { headers: { 'X-WP-Nonce': restNonce } } ];
  }, [ API, restNonce ] );
  const { data: swrPosts, mutate: mutatePosts } = useSWR(swrPostsKey, jsonFetcher);
  const { data: posts, error: swrPostsError } = useHandleSWR(swrPosts, [], true);
  useEffect(() => { dispatch({ type: SET_POSTS_MUTATE, mutatePosts }) }, [mutatePosts]);
  useEffect(() => { dispatch({ type: POSTS_UPDATED, posts }); }, [posts]);
  useEffect(() => { dispatch({ type: LOAD_ENTRY_ITEMS_UPDATED, key: loadEntryItemKeys.posts, data: posts }); }, [posts]);
  useEffect(() => { dispatch({ type: ERROR_UPDATED, apiError: swrPostsError }); }, [swrPostsError]);

  // Posts Metadata
  const swrPostsMetadataTypesKey = useMemo( () => {
    return [ `${API}/posts_metadata/`, { headers: { 'X-WP-Nonce': restNonce } } ];
  }, [ API, restNonce ] );
  const { data: swrPostsMetadata, mutate: mutatePostsMetadata } = useSWR(swrPostsMetadataTypesKey, jsonFetcher);
  const { data: postsMetadata, error: swrPostsMetadataError } = useHandleSWR(swrPostsMetadata, [], true);
  useEffect(() => { dispatch({ type: SET_POSTS_METADATA_MUTATE, mutatePostsMetadata }) }, [mutatePostsMetadata]);
  useEffect(() => { dispatch({ type: POSTS_METADATA_UPDATED, postsMetadata }); }, [postsMetadata]);
  useEffect(() => { dispatch({ type: LOAD_ENTRY_ITEMS_UPDATED, key: loadEntryItemKeys.postsMetadata, data: postsMetadata }); }, [postsMetadata]);
  useEffect(() => { dispatch({ type: ERROR_UPDATED, apiError: swrPostsMetadataError }); }, [swrPostsMetadataError]);

  // Users
  const swrUsersKey = useMemo( () => {
    return [ `${API}/users/`, { headers: { 'X-WP-Nonce': restNonce } } ];
  }, [ API, restNonce ] );
  const { data: swrUsers, mutate: mutateUsers } = useSWR(swrUsersKey, jsonFetcher);
  const { data: users, error: swrUsersError } = useHandleSWR(swrUsers, [], true);
  useEffect(() => { dispatch({ type: SET_USERS_MUTATE, mutateUsers }) }, [mutateUsers]);
  useEffect(() => { dispatch({ type: USERS_UPDATED, users }); }, [users]);
  useEffect(() => { dispatch({ type: LOAD_ENTRY_ITEMS_UPDATED, key: loadEntryItemKeys.users, data: users }); }, [users]);
  useEffect(() => { dispatch({ type: ERROR_UPDATED, apiError: swrUsersError }); }, [swrUsersError]);

  // Comments
  const swrCommentsKey = useMemo( () => {
    return [ `${API}/comments/`, { headers: { 'X-WP-Nonce': restNonce } } ];
  }, [ API, restNonce ] );
  const { data: swrComments, mutate: mutateComments } = useSWR(swrCommentsKey, jsonFetcher);
  const { data: comments, error: swrCommentsError } = useHandleSWR(swrComments, [], true);
  useEffect(() => { dispatch({ type: SET_COMMENTS_MUTATE, mutateComments }) }, [mutateComments]);
  useEffect(() => { dispatch({ type: COMMENTS_UPDATED, comments }); }, [comments]);
  useEffect(() => { dispatch({ type: LOAD_ENTRY_ITEMS_UPDATED, key: loadEntryItemKeys.comments, data: comments }); }, [comments]);
  useEffect(() => { dispatch({ type: ERROR_UPDATED, apiError: swrCommentsError }); }, [swrCommentsError]);

  // Transients
  const swrTransientsKey = useMemo( () => {
    return [ `${API}/transients/`, { headers: { 'X-WP-Nonce': restNonce } } ];
  }, [ API, restNonce ] );
  const { data: swrTransients, mutate: mutateTransients } = useSWR(swrTransientsKey, jsonFetcher);
  const { data: transients, error: swrTransientsError } = useHandleSWR(swrTransients, [], true);
  useEffect(() => { dispatch({ type: SET_TRANSIENTS_MUTATE, mutateTransients }) }, [mutateTransients]);
  useEffect(() => { dispatch({ type: TRANSIENTS_UPDATED, transients }); }, [transients]);
  useEffect(() => { dispatch({ type: LOAD_ENTRY_ITEMS_UPDATED, key: loadEntryItemKeys.transients, data: transients }); }, [transients]);
  useEffect(() => { dispatch({ type: ERROR_UPDATED, apiError: swrTransientsError }); }, [swrTransientsError]);

  // Options
  const { optionsFilters } = state;
  const swrOptionsKey = useMemo( () => {
    return state.shouldFetchOptions
      ? [`${API}/options/`, { headers: { 'X-WP-Nonce': restNonce } }]
      : null;
  }, [ API, restNonce, state.shouldFetchOptions ] );
  const { data: swrOptions, mutate: mutateOptions } = useSWR(swrOptionsKey, jsonFetcher);
  const { busy: busySwrOptions, data: options, error: swrOptionsError } = useHandleSWR(swrOptions, [], true);
  useEffect(() => { dispatch({ type: SET_OPTIONS_MUTATE, mutateOptions }) }, [mutateOptions]);
  useEffect(() => { dispatch({ type: busySwrOptions ? PUSH_BUSY_OPTIONS : POP_BUSY_OPTIONS }) }, [busySwrOptions]);
  useEffect(() => { dispatch({ type: OPTIONS_UPDATED, options }); }, [options]);
  useEffect(() => { dispatch({ type: LOAD_ENTRY_ITEMS_UPDATED, key: loadEntryItemKeys.options, data: options, itemKey: 'option_name' }); }, [options]);
  useEffect(() => { dispatch({ type: OPTIONS_TOTAL_UPDATED, options, search: optionsFilters.search }); }, [options, optionsFilters.search]);
  useEffect(() => { dispatch({
    type: FILTERED_OPTIONS_UPDATED,
    options, filterBy: optionsFilters.filterBy, limit: optionsFilters.limit, page: optionsFilters.page,
    hideUsedByWordPress: optionsFilters.hideUsedByWordPress, search: optionsFilters.search,
    accessor: optionsFilters.accessor, sort: optionsFilters.sort });
  },
    [options, optionsFilters.filterBy, optionsFilters.page, optionsFilters.limit, optionsFilters.hideUsedByWordPress,
      optionsFilters.search, optionsFilters.accessor, optionsFilters.sort]
  );
  useEffect(() => { dispatch({ type: ERROR_UPDATED, apiError: swrOptionsError }); }, [swrOptionsError]);

  // Cron jobs
  const { cronJobsFilters } = state;
  const swrCronJobsKey = useMemo( () => {
    return state.shouldFetchCronJobs
      ? [ `${API}/cron_jobs/`, { headers: { 'X-WP-Nonce': restNonce } } ]
      : null;
  }, [ API, restNonce, state.shouldFetchCronJobs ] );
  const { data: swrCronJobs, mutate: mutateCronJobs } = useSWR(swrCronJobsKey, jsonFetcher);
  const { busy: busyCronJobs, data: cronJobs, total: cronJobsTotal, error: swrCronJobsError } = useHandleSWR(swrCronJobs, [], true);
  useEffect(() => { dispatch({ type: SET_CRON_JOBS_MUTATE, mutateCronJobs }) }, [mutateCronJobs]);
  useEffect(() => { dispatch({ type: busyCronJobs ? PUSH_BUSY_CRON_JOBS : POP_BUSY_CRON_JOBS }) }, [busyCronJobs]);
  useEffect(() => { dispatch({ type: CRON_JOBS_UPDATED, cronJobs }); }, [cronJobs]);
  useEffect(() => { dispatch({ type: CRON_JOBS_TOTAL_UPDATED, cronJobs, search: cronJobsFilters.search }); }, [cronJobs, cronJobsFilters.search]);
  useEffect(() => { dispatch({
    type: FILTERED_CRON_JOBS_UPDATED,
    cronJobs, filterBy: cronJobsFilters.filterBy, limit: cronJobsFilters.limit, page: cronJobsFilters.page,
    hideUsedByWordPress: cronJobsFilters.hideUsedByWordPress, search: cronJobsFilters.search,
    accessor: cronJobsFilters.accessor, sort: cronJobsFilters.sort });
  },
    [cronJobs, cronJobsFilters.filterBy, cronJobsFilters.page, cronJobsFilters.limit, cronJobsFilters.hideUsedByWordPress,
      cronJobsFilters.search, cronJobsFilters.accessor, cronJobsFilters.sort]
  );
  useEffect(() => { dispatch({ type: ERROR_UPDATED, apiError: swrCronJobsError }); }, [swrCronJobsError]);

  // Installed Plugins
  const swrPluginsKey = useMemo( () => {
    return [ `${API}/plugins/`, { headers: { 'X-WP-Nonce': restNonce } } ];
  }, [ API, restNonce ] );
  const { data: swrPlugins, mutate: mutatePlugins } = useSWR(swrPluginsKey, jsonFetcher);
  const { busy: busyPlugins, data: plugins, error: swrPluginsError } = useHandleSWR(swrPlugins, [], true);
  useEffect(() => { dispatch({ type: SET_PLUGINS_MUTATE, mutatePlugins }) }, [mutatePlugins]);
  useEffect(() => { dispatch({ type: busyPlugins ? PUSH_BUSY : POP_BUSY }) }, [busyPlugins]);
  useEffect(() => { dispatch({ type: PLUGINS_UPDATED, plugins }); }, [plugins]);
  useEffect(() => { dispatch({ type: ERROR_UPDATED, apiError: swrPluginsError }); }, [swrPluginsError]);

  return (<DatabaseCleanerContext.Provider value={[state, dispatch]}>{children}</DatabaseCleanerContext.Provider>);
};

export default useDatabaseCleanerContext;